import PropTypes from "prop-types";
import React, { useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import { Button, Plus, Tag, Tag01 } from "@opendate-app/setlist";
import { EventHeaderField } from "./EventHeader";
import { toastError } from "../../../shared/toastHelper";

// Inline "+ Add tag" — replicates the confirm show page (EventDetails) tag popover:
// search/create against the team's tags and POST /teams/:id/tagged_items.
const csrfToken = () =>
  (typeof document !== "undefined" && document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")) || "";

const TagsField = ({ teamId, taggableId, initialTags, canManage }) => {
  const [tags, setTags] = useState(initialTags || []);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState("");
  const [removingIds, setRemovingIds] = useState([]);

  const loadAvailable = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken();
    axios.get(`/teams/${teamId}/tags`)
      .then((res) => setAvailable(res.data || []))
      .catch(() => toastError("Couldn't load tags. Please try again."));
  };

  const post = (taggedItem) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken();
    return axios.post(`/teams/${teamId}/tagged_items`, { tagged_item: taggedItem });
  };

  const remove = (tag) => {
    if (removingIds.includes(tag.id)) return;
    setRemovingIds((current) => [...current, tag.id]);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken();
    axios.delete(`/teams/${teamId}/tagged_items/${tag.id}`)
      .then(() => setTags((current) => current.filter((t) => t.id !== tag.id)))
      .catch(() => toastError("Couldn't remove that tag. Please try again."))
      .finally(() => setRemovingIds((current) => current.filter((id) => id !== tag.id)));
  };

  const addExisting = (tag, close) => {
    post({ taggable_type: "CalendarEvent", taggable_id: taggableId, tag_id: tag.id })
      .then((res) => { setTags((current) => [...current, { id: res.data?.id, name: tag.name }]); setSearch(""); close(); })
      .catch(() => toastError("Couldn't add that tag. Please try again."));
  };

  const createAndAdd = (name, close) => {
    post({ taggable_type: "CalendarEvent", taggable_id: taggableId, tag_attributes: { name } })
      .then((res) => {
        const tagId = res.data?.tag_id;
        if (tagId != null) setAvailable((current) => [...current, { id: tagId, name }]);
        setTags((current) => [...current, { id: res.data?.id, name }]);
        setSearch("");
        close();
      })
      .catch(() => toastError("Couldn't create that tag. Please try again."));
  };

  const query = search.trim();
  const lower = query.toLowerCase();
  const assigned = tags.map((t) => t.name.toLowerCase());
  const filtered = available.filter(
    (t) => !assigned.includes(t.name.toLowerCase()) && t.name.toLowerCase().includes(lower),
  );
  const canCreate = query.length > 0
    && !available.some((t) => t.name.toLowerCase() === lower)
    && !assigned.includes(lower);

  return (
    <EventHeaderField icon={Tag01} label="Tags">
      {tags.map((t) => (
        <Tag key={t.id ?? t.name} size="sm" dot onDismiss={canManage && t.id && !removingIds.includes(t.id) ? () => remove(t) : undefined}>{t.name}</Tag>
      ))}
      {canManage && (
        <Popup
          on="click"
          position="bottom left"
          arrow={false}
          className="add-tag-popup"
          contentStyle={{ width: "260px", padding: "8px", borderRadius: "8px", zIndex: 9999 }}
          onOpen={loadAvailable}
          trigger={<span><Button variant="tertiary" size="sm" icon={Plus}>Add tag</Button></span>}
        >
          {(close) => (
            <div className="add-tag-panel">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search or create a tag…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              <div className="add-tag-options">
                {filtered.map((t) => (
                  <button key={t.id} type="button" className="add-tag-option" onClick={() => addExisting(t, close)}>
                    {t.name}
                  </button>
                ))}
                {canCreate && (
                  <button type="button" className="add-tag-option add-tag-create" onClick={() => createAndAdd(query, close)}>
                    Create &ldquo;{query}&rdquo;
                  </button>
                )}
                {filtered.length === 0 && !canCreate && (
                  <div className="add-tag-empty">No tags</div>
                )}
              </div>
            </div>
          )}
        </Popup>
      )}
    </EventHeaderField>
  );
};

TagsField.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  taggableId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  initialTags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  canManage: PropTypes.bool,
};

TagsField.defaultProps = {
  initialTags: [],
  canManage: false,
};

export default TagsField;
